/**
 * Chat Mode System — Frontend Constants (ES Module mirror)
 *
 * 7 purposeful modes replacing the old 27-mode flat system.
 * Toggle state lives on the frontend and is sent per-message or on toggle-change.
 *
 * modeConfig object shape:
 * {
 *   mode: 'conversation',
 *   style: 'warning',
 *   toggles: { cursorLock: true, responseTimer: 45 }
 * }
 */

// ==================== MODE DEFINITIONS ====================

export const CHAT_MODES = {
    broadcast: {
        id: 'broadcast',
        type: 'broadcast',
        hasInput: false,
        name: 'Broadcast',
        icon: 'broadcast',
        description: 'One-way message delivery',
        styles: [
            { id: 'toast',       name: 'Toast',       icon: 'bell',        description: 'Notification popup' },
            { id: 'banner',      name: 'Banner',      icon: 'alert',       description: 'Full-width alert strip' },
            { id: 'typewriter',  name: 'Typewriter',  icon: 'type',        description: 'Character-by-character reveal' },
            { id: 'watermark',   name: 'Watermark',   icon: 'droplet',     description: 'Diagonal watermark text' },
            { id: 'whisper',     name: 'Whisper',     icon: 'whisper',     description: 'Random position whispers' },
            { id: 'glitch',      name: 'Glitch',      icon: 'zap',         description: 'RGB split glitch effect' },
            { id: 'matrix',      name: 'Matrix',      icon: 'code',        description: 'Matrix rain with message' },
            { id: 'redact',      name: 'Redact',      icon: 'block',       description: 'Redacted document reveal' },
            { id: 'spotlight',   name: 'Spotlight',   icon: 'spotlight',   description: 'Dim screen with lit message' },
            { id: 'subliminal',  name: 'Subliminal',  icon: 'flash',       description: 'Flash message (150ms)' },
            { id: 'crawl',       name: 'Crawl',       icon: 'ticker',      description: 'News ticker at bottom' },
            { id: 'overwrite',   name: 'Overwrite',   icon: 'overwrite',   description: 'Invasive text over screen' }
        ]
    },

    conversation: {
        id: 'conversation',
        type: 'interactive',
        hasInput: true,
        name: 'Conversation',
        icon: 'message-circle',
        description: 'Two-way chat with response input',
        styles: [
            { id: 'normal',   name: 'Normal',   description: 'Standard message' },
            { id: 'warning',  name: 'Warning',  description: 'Amber-bordered warning' },
            { id: 'critical', name: 'Critical', description: 'Red urgent message' },
            { id: 'whisper',  name: 'Whisper',  description: 'Faint, quiet message' }
        ]
    },

    interrogation: {
        id: 'interrogation',
        type: 'interactive',
        hasInput: true,
        name: 'Interrogation',
        icon: 'search',
        description: 'Spotlight interrogation room',
        styles: [
            { id: 'standard',  name: 'Standard',  description: 'Standard interrogation' },
            { id: 'dictation', name: 'Dictation', description: 'Must type exact text back' }
        ]
    },

    decree: {
        id: 'decree',
        type: 'interactive',
        hasInput: false,
        name: 'Decree',
        icon: 'gavel',
        description: 'Forced-choice button response',
        styles: [
            { id: 'clean',       name: 'Clean',       description: 'Minimal, no effects' },
            { id: 'dramatic',    name: 'Dramatic',    description: 'Spotlight and glow' },
            { id: 'threatening', name: 'Threatening', description: 'Red tint, scan lines' }
        ]
    },

    terminal: {
        id: 'terminal',
        type: 'interactive',
        hasInput: true,
        name: 'Terminal',
        icon: 'terminal',
        description: 'CLI-style prompt',
        styles: [
            { id: 'green', name: 'Green', description: 'Classic green-on-black' },
            { id: 'amber', name: 'Amber', description: 'Amber phosphor' },
            { id: 'white', name: 'White', description: 'White on black' },
            { id: 'red',   name: 'Red',   description: 'Red alert terminal' }
        ]
    },

    chaos: {
        id: 'chaos',
        type: 'interactive',
        hasInput: true,
        name: 'Chaos',
        icon: 'shuffle',
        description: 'Multi-window popup storm',
        styles: []
    }
};

// ==================== TOGGLE SCHEMAS ====================

export const MODE_TOGGLES = {
    broadcast: {
        defaultStyle:       { type: 'select', default: 'toast', options: ['toast', 'banner', 'typewriter', 'watermark', 'whisper', 'glitch', 'matrix', 'redact', 'spotlight', 'subliminal', 'crawl', 'overwrite'], group: 'delivery', label: 'Default Style' },
        clickThrough:       { type: 'boolean', default: true, group: 'delivery', label: 'Click Through' },
        stackMessages:      { type: 'boolean', default: false, group: 'delivery', label: 'Stack Messages' },
        durationMultiplier: { type: 'range', default: 1, min: 0.5, max: 5, step: 0.5, group: 'delivery', label: 'Duration Multiplier' },
        soundOnDelivery:    { type: 'boolean', default: false, group: 'delivery', label: 'Sound on Delivery' },
        requireAcknowledge: { type: 'boolean', default: false, group: 'delivery', label: 'Require Acknowledgment' }
    },

    conversation: {
        cursorLock:        { type: 'boolean', default: false, group: 'response', label: 'Lock Cursor to Window' },
        mustRespond:       { type: 'boolean', default: false, group: 'response', label: 'Must Respond' },
        responseTimer:     { type: 'range', default: 0, min: 0, max: 300, step: 15, group: 'response', label: 'Response Timer (sec)', description: '0 = no timer' },
        minLength:         { type: 'range', default: 0, min: 0, max: 500, step: 10, group: 'response', label: 'Min Response Length', description: '0 = no minimum' },
        noBackspace:       { type: 'boolean', default: false, group: 'response', label: 'Disable Backspace' },
        typingVisibility:  { type: 'boolean', default: true, group: 'surveillance', label: 'See Their Typing' },
        readReceipts:      { type: 'boolean', default: true, group: 'surveillance', label: 'Read Receipts' },
        idleDetection:     { type: 'boolean', default: false, group: 'surveillance', label: 'Idle Detection' },
        focusTracking:     { type: 'boolean', default: false, group: 'surveillance', label: 'Focus Tracking' },
        screenshotOnOpen:  { type: 'boolean', default: false, group: 'surveillance', label: 'Screenshot on Open' },
        windowPosition:    { type: 'select', default: 'BR', options: ['BR', 'BL', 'TR', 'TL', 'center'], group: 'window', label: 'Window Position' },
        windowSize:        { type: 'select', default: 'standard', options: ['compact', 'standard', 'large'], group: 'window', label: 'Window Size' },
        dimBackground:     { type: 'boolean', default: false, group: 'window', label: 'Dim Background' },
        flashForAttention: { type: 'boolean', default: true, group: 'window', label: 'Flash for Attention' },
        soundAlerts:       { type: 'boolean', default: false, group: 'display', label: 'Sound Alerts' },
        senderPersona:     { type: 'text', default: '', group: 'display', label: 'Sender Name Override' },
        showMyTyping:      { type: 'boolean', default: true, group: 'display', label: 'Show My Typing' },
        disableInput:      { type: 'boolean', default: false, group: 'control', label: 'Disable Input' },
        delayedDelivery:   { type: 'range', default: 0, min: 0, max: 30, step: 1, group: 'control', label: 'Delayed Delivery (sec)' },
        autoRecall:        { type: 'range', default: 0, min: 0, max: 60, step: 5, group: 'control', label: 'Auto-Recall (sec)', description: '0 = no recall' },
        pinMessage:        { type: 'boolean', default: false, group: 'control', label: 'Pin Message' },
        emotionTags:       { type: 'boolean', default: false, group: 'control', label: 'Emotion Tags' },
        expandContract:    { type: 'boolean', default: false, group: 'control', label: 'Expand/Contract Window' },
        // Display
        forcedPersona:     { type: 'text', default: '', group: 'display', label: 'Subject Display Name', placeholder: 'e.g. Subject #4471' }
    },

    interrogation: {
        responseTimer:        { type: 'range', default: 60, min: 10, max: 300, step: 10, group: 'pressure', label: 'Response Timer (sec)' },
        anxietyRamp:          { type: 'boolean', default: true, group: 'pressure', label: 'Anxiety Ramp' },
        lockCursorToInput:    { type: 'boolean', default: true, group: 'pressure', label: 'Lock Cursor to Input' },
        blockAltTab:          { type: 'boolean', default: true, group: 'pressure', label: 'Block Alt+Tab' },
        scanLines:            { type: 'boolean', default: true, group: 'atmosphere', label: 'Scan Lines' },
        spotlightStyle:       { type: 'select', default: 'circle', options: ['circle', 'bar', 'none'], group: 'atmosphere', label: 'Spotlight Style' },
        failureConsequence:   { type: 'select', default: 'none', options: ['none', 'repeat', 'escalate'], group: 'consequence', label: 'On Failure' },
        repeatUntilAnswered:  { type: 'boolean', default: false, group: 'consequence', label: 'Repeat Until Answered' }
    },

    decree: {
        options:            { type: 'array', default: ['Yes', 'No'], minItems: 2, maxItems: 4, group: 'options', label: 'Button Labels' },
        timer:              { type: 'range', default: 0, min: 0, max: 120, step: 5, group: 'pressure', label: 'Timer (sec)', description: '0 = no timer' },
        defaultOnTimeout:   { type: 'select', default: '', options: [], group: 'pressure', label: 'Default on Timeout', description: 'Auto-select when timer expires' },
        lockScreen:         { type: 'boolean', default: true, group: 'display', label: 'Lock Screen' },
        confirmation:       { type: 'boolean', default: false, group: 'display', label: 'Require Confirmation' },
        consequencePreview: { type: 'object', default: {}, group: 'display', label: 'Consequence Previews' }
    },

    terminal: {
        bootSequence:   { type: 'boolean', default: true, group: 'display', label: 'Boot Sequence' },
        promptPrefix:   { type: 'text', default: '> ', group: 'display', label: 'Prompt Prefix' },
        echoInput:      { type: 'boolean', default: true, group: 'display', label: 'Echo Input' },
        autoScroll:     { type: 'boolean', default: true, group: 'display', label: 'Auto-Scroll' },
        commandHistory: { type: 'boolean', default: true, group: 'display', label: 'Command History' },
        fakeCommands:   { type: 'boolean', default: false, group: 'display', label: 'Fake System Commands' },
        windowSize:     { type: 'select', default: 'standard', options: ['compact', 'standard', 'large'], group: 'window', label: 'Window Size' }
    },

    chaos: {
        windowCount:            { type: 'range', default: 6, min: 3, max: 12, step: 1, group: 'chaos', label: 'Window Count' },
        driftSpeed:             { type: 'select', default: 'medium', options: ['slow', 'medium', 'fast', 'frantic'], group: 'chaos', label: 'Drift Speed' },
        teleport:               { type: 'boolean', default: true, group: 'chaos', label: 'Teleport Windows' },
        spawnNew:               { type: 'boolean', default: true, group: 'chaos', label: 'Spawn New Windows' },
        mirrorText:             { type: 'boolean', default: false, group: 'chaos', label: 'Mirror Text' },
        inputWindowHighlight:   { type: 'boolean', default: true, group: 'chaos', label: 'Highlight Input Window' },
        soundOnSpawn:           { type: 'boolean', default: false, group: 'chaos', label: 'Sound on Spawn' },
        collapseTrigger:        { type: 'select', default: 'response', options: ['response', 'timer', 'manual'], group: 'chaos', label: 'Collapse Trigger' }
    }
};

// ==================== DEFAULT TOGGLES ====================

export const DEFAULT_TOGGLES = {};
for (const [mode, toggles] of Object.entries(MODE_TOGGLES)) {
    DEFAULT_TOGGLES[mode] = {};
    for (const [key, schema] of Object.entries(toggles)) {
        DEFAULT_TOGGLES[mode][key] = schema.default;
    }
}

// ==================== HELPERS ====================

export function resolveToggles(mode, overrides = {}) {
    const defaults = DEFAULT_TOGGLES[mode];
    if (!defaults) return overrides;
    return { ...defaults, ...overrides };
}

export function normalizeModeConfig(modeConfig) {
    if (!modeConfig) return { mode: 'conversation', style: null, toggles: {} };
    if (typeof modeConfig === 'string') return { mode: modeConfig, style: null, toggles: {} };

    const { mode = 'conversation', style = null, toggles = {} } = modeConfig;

    if (!CHAT_MODES[mode]) {
        return { mode: 'conversation', style: null, toggles: {} };
    }

    return { mode, style, toggles };
}

/**
 * Get toggle groups for a mode (for rendering toggle panel sections)
 */
export function getToggleGroups(mode) {
    const toggles = MODE_TOGGLES[mode];
    if (!toggles) return [];

    const groups = new Map();
    for (const [key, schema] of Object.entries(toggles)) {
        const group = schema.group || 'general';
        if (!groups.has(group)) {
            groups.set(group, []);
        }
        groups.get(group).push({ key, ...schema });
    }

    return Array.from(groups.entries()).map(([name, items]) => ({ name, items }));
}
